import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../../../Utils/AxiosUtil';
import {
	Alert as ServaAlert,
	Card,
	Chart as KitChart,
	downloadGraphicalExcel,
	IconBar,
	Skeleton,
	USMapChart,
} from '@servahealth/serva-ui-kit';
import useAuth from '../../../../Hooks/useAuth';
import DebugQuery from '../../../../Interfaces/Reports/DebugQuery';
import ItemIdMap from '../../../../Interfaces/Reports/ItemIdMap';
import Reports from '../../../../Constants/Endpoints/Reports';
import ReportItemNoData from '../ReportItemNoData';
import { getServaChartOptionsFromServaChartType,
		 translateServaChartTypeToChartJSChartType,
		 translateServaChartisStackedToChartJSisStacked,
		 transformServaChartToChartJSChart as transformServaChartDataToChartJSData, } from '../../../../Utils/ChartUtil';
import { ServaChartOptions } from '../../../../Interfaces/Chart/Type';
import PostChartDataUsingIDsV2 from '../../../../Interfaces/Requests/Reports/GetChartDataUsingIDsV2';
import ServaChart from '../../../../Interfaces/Responses/Reports/Charts/Chart';
import ChartJSChartData from '../../../../Interfaces/Responses/Reports/Charts/ChartData';
import { populateChartInputs } from '../ReportItemHelper';

interface ChartItemProps extends ItemIdMap {
	handleBarClick: (
		dataSetIndex: number,
		index: number,
		chartData: ChartJSChartData
	) => void;
}

const ChartItem = ({ handleBarClick, ...item }: ChartItemProps) => {
	const { user } = useAuth();
	const chartParameters = item.chartParameters;
	const hasAppliedFilters = item.filterInputs?.some((filterInput) => filterInput.hasValue) ?? false;
	const [height, setHeight] = useState<any>(undefined);
	const [debugQueryVisible, setDebugQueryVisible] = useState(false);
	const [debugQuery, setDebugQuery] = useState<DebugQuery>();
	const [width, setWidth] = useState(undefined);
	const div = useCallback<any>((node: any) => {
		if (!!node && !!node.parentNode) {
			setHeight(node.parentNode.offsetHeight - 49);
			setWidth(node.parentNode.offsetWidth);
		}
	}, []);
	
	const { isLoading: isChartDataLoading, isError: isChartDataError, error: getChartDataError, data: chartData, } = useQuery(
		['chart', chartParameters?.chartId, item.filterInputs],
		async () => {
			if (chartParameters) {
				const url = `${process.env.REACT_APP_API_ROOT}${Reports.GET_CHART_DATA_USING_IDS_V2}`;
				
				const inputs = populateChartInputs(
					item.chartParameters,
					item.inputMaps,
					item.filterInputs
				);
				
				const requestData: PostChartDataUsingIDsV2 = {
					currentToken: user?.runningUser.currentToken ?? '',
					chartID: chartParameters?.chartId ?? -1,
					tenantID: chartParameters?.tenantId ?? -1,
					inputs: inputs,
				};
				
				const response = await AxiosUtil.post<ServaChart, PostChartDataUsingIDsV2>(url, requestData);

				return response;
			}
			else
			{
				return null;
			}
		},
		{
			select: (response: ServaChart | null) => {
				if (!response || !chartParameters) {
					return null;
				}

				const servaChartOptions: ServaChartOptions = getServaChartOptionsFromServaChartType(
					response.dataSets[0].reportChartType
				);

				const stackedOptions = {
					scales: {
						x: {
							stacked: true,
						},
						y: {
							stacked: true,
						},
					},
				};

				if (servaChartOptions.type) {
					const chartData = transformServaChartDataToChartJSData(
						response,
						servaChartOptions,
						response.title
					);

					// calculate the aspect ratio for chartjs based on parent height/width
					// sub 21 pixels from height to account for title of the chart filling parent height
					let aspectRatio: number | undefined =
						((width && width) ?? 1) /
						((height && height) ?? 1);
					if (
						(width && width < 70) ||
						(height && height < 70) ||
						(!height && !width)
					)
						aspectRatio = undefined; // if the calculated height or width is too low, set default aspect ratio to 1 for pie/doughnut

					return {
						id: chartParameters.reportItemId,
						type: 'chart',
						dataSets: response.dataSets,
						dataPointLabel: chartData.dataPointLabel,
						chartType: translateServaChartTypeToChartJSChartType(servaChartOptions),//ChartTypes[chartType.type].startsWith("stacked") ? ChartTypes[chartType.type].substring(7).toLowerCase() : ChartTypes[chartType.type],
						showLegend: servaChartOptions.showLegend,
						chartData,
						chartOptions: {
							indexAxis: servaChartOptions.vertical ? 'x' : 'y',
							title: {
								display: true,
								text: response.title,
							},
							maintainAspectRatio: !!aspectRatio,
							aspectRatio,
							responsive: true,
							...(translateServaChartisStackedToChartJSisStacked(servaChartOptions)
								? stackedOptions
								: {}),
						},
						onDataSetBarClick: { handleBarClick, ...item },
						chartParameters: chartParameters,
						queryDebug: response.queryDebug
					};
				}

				return null;
			},
			onSuccess: (response: any) => {
				if (!response || !chartParameters) {
					return;
				}

				setDebugQuery({
					queryReportId: chartParameters.reportId,
					queryReportName: response.title, //response.chartData.title,
					queryReportPageId: chartParameters.reportPageId,
					queryReportItemId: chartParameters.reportItemId,
					queryChartId: chartParameters.chartId,
					queryDataSetId: chartParameters.dataSetId,
					queryString: response.queryDebug
				});

			},
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			enabled: !!chartParameters && !!height && !!width,
		}
	);

	function ConvertChartDataToExcelData (ChartData: typeof chartData): any
	{
		const excelDataArray: Record<string, string>[] = [];

		if (ChartData && ChartData.chartData)
		{
			let excelData: Record<string, string> = {
			};
	
			for (var i = 0; i < ChartData?.chartData?.datasets.length; i++) {
				if (ChartData.chartOptions.indexAxis === 'x')
				{
					excelData['X-Axis'] = ChartData?.chartData?.datasets[i].label ?? '';
				}
				else
				{
					excelData['Y-Axis'] = ChartData?.chartData?.datasets[i].label ?? '';
				}

				for (var j = 0; j < ChartData.chartData.datasets[i].data.length; j++) {
					excelData[ChartData.chartData.labels[j]] = ChartData?.chartData?.datasets[i].data[j].toString();
				};

				excelDataArray.push(JSON.parse(JSON.stringify(excelData)));
			}
		}

		return excelDataArray;
	}

	const iconBarArgsAdmin: any = {
		icons: [
			{
				type: 'excel',
				tooltipText: 'download excel',
				onClick: () => {
					if (chartData && chartData.chartData)
					{
						downloadGraphicalExcel(
							chartData?.chartOptions.indexAxis,
							chartData?.chartData?.labels,
							ConvertChartDataToExcelData(chartData), //chartData?.chartData?.datasets[0].data,
							chartData?.chartParameters?.reportName ?? 'rpt_excel',
							chartData?.chartData?.title ?? 'chart'
						)
					}
				}
			},
			{
				type: 'debug',
				tooltipText: 'report debug',
				onClick: () => setDebugQueryVisible(true),
			},
		],
		orientation: 'horizontal',
		spacing: 30,
	};
	const iconBarArgs: any = {
		icons: [
			{
				type: 'excel',
				tooltipText: 'download excel',
				onClick: () => {
					if (chartData && chartData.chartData)
					{
						downloadGraphicalExcel(
							chartData?.chartOptions.indexAxis,
							chartData?.chartData?.labels,
							ConvertChartDataToExcelData(chartData), //chartData?.chartData?.datasets[0].data,
							chartData?.chartParameters?.reportName ?? 'rpt_excel',
							chartData?.chartData?.title ?? 'chart'
						)
					}
				}
			},
		],
		orientation: 'horizontal',
		spacing: 30,
	};

	const warningContent = useMemo(() => {
		const errorMsg: any = getChartDataError;

		if (isChartDataError)
			return (
				<ServaAlert
					message={errorMsg?.response?.data?.detail}
					type="error"
					showIcon={true}
					className={'mb-2'}
				/>
			);

		if (!chartData || isChartDataLoading) {
			return <Skeleton active />;
		}
		else if (!chartData?.chartData?.datasets || chartData.chartData?.datasets.some((x) => !x.data)) {
			return (
				<ReportItemNoData
					debugQuery={debugQuery}
					debugQueryVisible={debugQueryVisible}
					hasFilters={hasAppliedFilters}
					queryLabel="Chart Query"
					showDebug={user?.runningUser.isSecurityAdmin}
					onCloseDebug={() => setDebugQueryVisible(false)}
				/>
			);
		}
	}, [chartData, debugQuery, debugQueryVisible, getChartDataError, hasAppliedFilters, isChartDataError, isChartDataLoading, user?.runningUser.isSecurityAdmin]);

	const content = useMemo(() => {
		const kitChartOptions = { ...(chartData?.chartOptions ?? {}), title: undefined }

		const usMapDivId = `usMapDiv_${chartParameters?.chartId}`;

		if (isChartDataLoading || !chartData?.chartData)
			return undefined;

		return (
			<div>
				<ReportItemNoData
					debugOnly={true}
					debugQuery={debugQuery}
					debugQueryVisible={debugQueryVisible}
					hasFilters={hasAppliedFilters}
					queryLabel="Chart Query"
					showDebug={user?.runningUser.isSecurityAdmin}
					onCloseDebug={() => setDebugQueryVisible(false)}
				/>
				{chartData?.chartData?.isMap ? (
					<div id={usMapDivId}>
						<USMapChart
							target={usMapDivId}
							title={chartData.chartData.title}
							dataPoints={chartData.chartData.dataPoints}
						/>
					</div>
				) : (
					chartData && (
						<KitChart
							dataPointLabel={chartData.dataPointLabel}
							type={chartData.chartType}
							data={chartData.chartData}
							options={kitChartOptions}
							showLegend={chartData.showLegend}
							onDataSetBarClick={handleBarClick}
						/>
					)
				)}
			</div>
		)
	}, [chartData, chartParameters?.chartId, debugQuery, debugQueryVisible, handleBarClick, hasAppliedFilters, isChartDataLoading, user?.runningUser.isSecurityAdmin])

	return (
		<Card 
			className={`report-chart-card ${(chartData?.chartData?.datasets[0]?.drilldowns?.length ?? 0) > 0 ? 'shadow-card-drilldown' : 'shadow-card'}`}
			size='small' 
			style={{ height: '100%', boxSizing: 'border-box' }}
			styles={{ body: { height: !!height ? height : '100%', overflow: 'hidden', padding: 0 } }}
			title={chartData?.chartData?.title} 
			extra={(
				user?.runningUser.isSecurityAdmin ?
					<div><IconBar size='small' {...iconBarArgsAdmin} /></div> : 
					<div><IconBar size='small' {...iconBarArgs} /></div>
			)}
		>
			{warningContent}
			<div className='chart-item' ref={div} style={{ height: !!height ? height : '100%' }}>
				{content}
			</div>
		</Card>
	);
};

export default ChartItem;
